import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { StarDisplay } from "@/components/Stars";

const VENDOR_LABELS = {
  punctuality: "Punctuality",
  communication: "Communication",
  cleanliness: "Cleanliness",
  booth: "Booth",
  product: "Product",
};

const ORGANIZER_LABELS = {
  ease_of_finding: "Ease of locating",
  attendees: "Attendees",
  sales: "Sales",
  location: "Location",
  parking: "Parking",
  organizer_communication: "Organizer communication",
  coordination: "Coordination",
  venue: "Venue & placement",
  communication: "Communication",
};

function DeltaChip({ trend }) {
  if (!trend || trend.direction === null) {
    return (
      <span className="text-xs" style={{ color: "var(--ink-soft)" }} data-testid="self-trend-delta-none">
        Not enough monthly data yet
      </span>
    );
  }
  const config = {
    up: { Icon: TrendingUp, color: "var(--moss)", label: "trending up" },
    down: { Icon: TrendingDown, color: "var(--terracotta)", label: "trending down" },
    flat: { Icon: Minus, color: "var(--ink-soft)", label: "steady" },
  }[trend.direction] || { Icon: Minus, color: "var(--ink-soft)", label: "steady" };
  const { Icon, color, label } = config;
  const deltaLabel = trend.delta > 0 ? `+${trend.delta}` : `${trend.delta}`;
  return (
    <span
      data-testid={`self-trend-delta-${trend.direction}`}
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold px-2 py-1 rounded-full"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      <Icon className="h-3.5 w-3.5" /> {deltaLabel} vs. last month · {label}
    </span>
  );
}

export default function SelfRatingsTrend({ role = "vendor" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ratings/self").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading your trend…</div>;
  if (!data) return null;

  if (!data.eligible) {
    return (
      <div
        data-testid="self-trend-not-eligible"
        className="card-warm rounded-sm p-8 text-center"
      >
        <div className="font-serif text-2xl" style={{ color: "var(--ink)" }}>
          {data.count === 0 ? "No ratings yet." : `${data.count} of ${data.min_required} ratings collected.`}
        </div>
        <p className="mt-3 text-sm max-w-md mx-auto leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          Once you&rsquo;ve gathered at least <strong>{data.min_required}</strong> ratings we&rsquo;ll show you a
          private aggregate trend — never individual comments or names. That&rsquo;s how we keep community feedback honest
          while still giving you a signal on how you&rsquo;re doing.
        </p>
      </div>
    );
  }

  const labels = role === "vendor" ? VENDOR_LABELS : ORGANIZER_LABELS;
  const { summary, trend, distribution, count } = data;
  const maxDist = Math.max(...Object.values(distribution || {}));

  return (
    <div className="space-y-8" data-testid="self-trend">
      <div className="card-warm rounded-sm p-6" style={{ borderLeft: "3px solid var(--moss)" }}>
        <div className="overline" style={{ color: "var(--moss)" }}>Private aggregate — only you see this</div>
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
          Individual comments and rater identities are never shown to you — only the aggregate below and its trend
          from last month. Public visitors see your ratings normally.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>Overall</div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="font-serif text-6xl leading-none" data-testid="self-trend-overall">
              {summary.overall.toFixed(1)}
            </div>
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              /5 · {count} ratings
            </div>
          </div>
          <div className="mt-2"><StarDisplay value={summary.overall} size={20} /></div>
          <div className="mt-4"><DeltaChip trend={trend} /></div>
          {trend.this_month_overall !== null && (
            <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
              This month: <strong>{trend.this_month_overall}</strong> ({trend.this_month_count} rating{trend.this_month_count === 1 ? "" : "s"})
              {trend.prev_month_overall !== null && (
                <> · Last month: <strong>{trend.prev_month_overall}</strong> ({trend.prev_month_count})</>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>By category</div>
          <div className="mt-3 space-y-2">
            {Object.entries(labels).map(([k, label]) => {
              const v = summary.dimensions?.[k] || 0;
              return (
                <div key={k} className="flex items-center justify-between gap-4" data-testid={`self-trend-dim-${k}`}>
                  <span className="text-sm">{label}</span>
                  <span className="inline-flex items-center gap-2">
                    <StarDisplay value={v} size={14} />
                    <span className="text-xs w-8 text-right" style={{ color: "var(--ink-soft)" }}>
                      {v > 0 ? v.toFixed(1) : "—"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {distribution && (
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>Score distribution</div>
          <div className="mt-3 space-y-2" data-testid="self-trend-distribution">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = distribution[String(n)] || 0;
              const pct = maxDist > 0 ? (c / maxDist) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-3 text-xs" data-testid={`self-trend-dist-${n}`}>
                  <span className="w-12 flex items-center gap-1" style={{ color: "var(--ink-soft)" }}>
                    {n} <span style={{ color: "var(--terracotta)" }}>★</span>
                  </span>
                  <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: "var(--sand)" }}>
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: "var(--terracotta)" }}
                    />
                  </div>
                  <span className="w-8 text-right font-medium" style={{ color: "var(--ink-soft)" }}>{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
